/**
 * How a metric value, a delta and a period caption print on the web (#2175 B2).
 * The phone prints with its one-Swiss-rule formatter; the web prints through
 * `lib/format.ts` and `formatMoneyCents`, so the numbers agree and only the
 * locale details (decimal mark, money decimals) follow the web standard.
 */

import type { I18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { formatDecimal, formatDuration, formatMonthYear, formatNumber, formatPercent } from "@/lib/format";
import { formatMoneyCents } from "@/lib/money";
import type { StatDelta } from "@/components/ui/stat-card";
import type { MetricDelta } from "./period";
import type { MetricAggregate, MetricDefinition, MetricUnit } from "./types";

/** A value as a card or a table cell shows it; "" when it is not known. */
export function formatMetricValue(
  unit: MetricUnit,
  value: number | null | undefined,
  i18n: I18n,
  currency: string,
): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "";
  const locale = i18n.locale;
  switch (unit) {
    case "chf":
      return formatMoneyCents(Math.round(value), currency);
    case "percent":
      return formatPercent(value / 100, locale, 1);
    case "hours":
      return i18n._(
        msg({ id: "analytics.metrics.unit.hours", message: "{hours} h" }),
        { hours: formatNumber(value, locale, { maximumFractionDigits: 1 }) },
      );
    case "days":
      return i18n._(
        msg({
          id: "analytics.metrics.unit.days",
          message: "{count, plural, one {{days} day} other {{days} days}}",
        }),
        { count: value, days: formatNumber(value, locale, { maximumFractionDigits: 1 }) },
      );
    case "minutes":
      return formatDuration(value, locale);
    case "ratio":
      return i18n._(msg({ id: "analytics.metrics.unit.ratio", message: "1 : {ratio}" }), {
        ratio: formatDecimal(value, locale, 1),
      });
    case "decimal":
      return formatDecimal(value, locale, 1);
    case "count":
    default:
      return Number.isInteger(value) ? formatNumber(value, locale) : formatDecimal(value, locale, 1);
  }
}

/**
 * The delta badge of a card: the change in percent, or in percentage points for
 * a rate. Up reads as good unless the metric says lower is better.
 */
export function metricStatDelta(
  def: MetricDefinition,
  delta: MetricDelta | undefined,
  i18n: I18n,
): StatDelta | undefined {
  if (!delta || !Number.isFinite(delta.value)) return undefined;
  const locale = i18n.locale;
  const direction = Math.abs(delta.value) < 0.05 ? "flat" : delta.value > 0 ? "up" : "down";
  const sign = delta.value > 0 ? "+" : "";
  const value =
    delta.kind === "pts"
      ? i18n._(msg({ id: "analytics.metrics.delta.points", message: "{value} pts" }), {
          value: `${sign}${formatDecimal(delta.value, locale, 1)}`,
        })
      : `${sign}${formatPercent(delta.value / 100, locale, 1)}`;
  if (direction === "flat") return { value, direction };
  const good = def.deltaInverse ? direction === "down" : direction === "up";
  return { value, direction, tone: good ? "success" : "danger" };
}

/**
 * "Jan 2026 - Oct 2026", or one month when from equals to. The year keeps four
 * digits so a range never reads as days of a month.
 */
export function periodLabel(from: string, to: string, locale: string): string {
  if (from === to) return formatMonthYear(from, locale, "long");
  return `${formatMonthYear(from, locale, "medium")} \u2013 ${formatMonthYear(to, locale, "medium")}`;
}

/**
 * The caption under a period total: "Total Jan 2026 - Oct 2026" for a sum,
 * "Ø Jan 2026 - Oct 2026" (the mean sign, as on the phone) for a mean, "As of end of October 2026" for a stock.
 */
export function periodTotalCaption(
  kind: MetricAggregate,
  from: string,
  to: string,
  lastPeriod: string | undefined,
  i18n: I18n,
): string {
  const locale = i18n.locale;
  if (kind === "last" && lastPeriod) {
    return i18n._(msg({ id: "analytics.metrics.total.last", message: "As of end of {month}" }), {
      month: formatMonthYear(lastPeriod, locale, "long"),
    });
  }
  const period = periodLabel(from, to, locale);
  if (kind === "mean") {
    return from === to
      ? period
      : i18n._(msg({ id: "analytics.metrics.periodMean", message: "\u00d8 {period}" }), { period });
  }
  return i18n._(msg({ id: "analytics.metrics.total.period", message: "Total {period}" }), { period });
}
